import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { OrgAdminDashboardComponent } from '../dashboard/dashboard.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { RolesApiService, Role } from '@angular-workspace/services';
import { ToasterService } from '../../services/toaster.service';
import { WorkspaceContextService } from '../../services/workspace-context.service';

@Component({
  selector: 'app-org-admin-home',
  standalone: true,
  imports: [CommonModule, OrgAdminDashboardComponent, HeaderComponent],
  template: `
    <app-header [forceLightBackground]="true"></app-header>
    <div *ngIf="isChecking" class="loading-container">
      <div class="loading-spinner"></div>
    </div>
    <app-org-admin-dashboard *ngIf="canAccess && !isChecking"></app-org-admin-dashboard>
  `,
  styles: [`
    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: #ffffff;
    }
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #f3f4f6;
      border-top: 4px solid #059669;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `]
})
export class OrgAdminHomeComponent implements OnInit {
  private rolesService = inject(RolesApiService);
  private router = inject(Router);
  private toasterService = inject(ToasterService);
  private workspaceContext = inject(WorkspaceContextService);
  
  canAccess = false;
  isChecking = true;

  async ngOnInit(): Promise<void> {
    await this.checkOrgAdminRole();
  }

  private async checkOrgAdminRole(): Promise<void> {
    try {
      try {
        await this.workspaceContext.loadDelegatedWorkspaces();
      } catch {
        /* use whatever workspace cache we already have */
      }
      if (this.workspaceContext.getActiveDelegatedPanelRole() === Role.ORG_ADMIN) {
        this.canAccess = true;
        return;
      }

      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      console.log('OrgAdminHomeComponent - User roles:', userRoles);

      if (userRoles && userRoles.length > 0) {
        // Get the highest role panel route first
        const highestRolePanelRoute = this.rolesService.getHighestRolePanelRoute(userRoles);
        console.log('OrgAdminHomeComponent - Highest role panel route:', highestRolePanelRoute);
        
        // Check if user has org_admin role (check both org_admin and org-admin variants)
        const hasOrgAdminRole = userRoles.some(role => {
          const normalizedRole = role.toLowerCase().replace(/_/g, '-');
          return normalizedRole === 'org-admin' || normalizedRole === Role.ORG_ADMIN;
        });
        
        // Also check if user has cos_admin role (which should redirect to admin panel)
        const hasCosAdminRole = userRoles.some(role => {
          const normalizedRole = role.toLowerCase().replace(/_/g, '-');
          return normalizedRole === 'cos-admin' || normalizedRole === 'cos_admin';
        });
        
        // If user has cos_admin role, they should go to admin panel, not org-admin
        if (hasCosAdminRole && highestRolePanelRoute.startsWith('/admin')) {
          console.log('User has cos_admin role, redirecting to admin panel:', highestRolePanelRoute);
          this.toasterService.warning("You don't have access to some of the panels.");
          this.router.navigate([highestRolePanelRoute], { replaceUrl: true });
          return;
        }
        
        if (!hasOrgAdminRole) {
          // User doesn't have org_admin role, redirect to their highest role panel
          console.log('User does not have org_admin role, redirecting to highest role panel:', highestRolePanelRoute);
          this.toasterService.warning("You don't have access to some of the panels.");
          this.router.navigate([highestRolePanelRoute], { replaceUrl: true });
          return;
        }
        
        // User has org_admin role, allow access
        this.canAccess = true;
      } else {
        // No roles found, redirect to home
        this.toasterService.warning("You don't have access to some of the panels.");
        this.router.navigate(['/'], { replaceUrl: true });
      }
    } catch (error) {
      console.error('Error checking roles:', error);
      // On error, redirect to home
      this.toasterService.warning("You don't have access to some of the panels.");
      this.router.navigate(['/'], { replaceUrl: true });
    } finally {
      this.isChecking = false;
    }
  }
}
